/**
 * The guest demo's walls, client side (AD-794 §3.2, §6.2; story
 * `q-guest-demo-s5-client-guest-mode-and-end-card`).
 *
 * The SERVER is the wall (S2: a walled message type is answered `error guest_wall` before any
 * handler). The client's job is to be graceful about it:
 *
 *   1. HIDE the walled affordances rather than let them fail — the Shell launcher and hotkeys
 *      offer only the panels a guest can use (`GUEST_PANEL_ACCESS`), and the HUD drops the chat
 *      dock and the Gleam pill (`Hud.tsx`).
 *   2. When something still reaches the wall (a hotkey for a panel a guest can see but whose one
 *      button is walled, e.g. a specialisation choice), answer with ONE calm line, not an error
 *      per press: `WallNotifier` de-duplicates per message type and rate-limits overall.
 *
 * Fail closed: a panel id this table does not name is hidden for guests, so a panel added later
 * never silently appears in the demo (`guestWalls.test.ts` also pins the table to the registry
 * in `ui/panels/index.tsx`, so a new panel must be classified on purpose).
 */

export type GuestPanelAccess = 'allowed' | 'walled';

/**
 * Every Shell panel id (`ui/panels/index.tsx` `PANELS`) → whether a guest sees it. Allowed is the
 * design's verb list (§3): the character and its kit, quests (Sel's dialogue renders there),
 * crafting, and the client-local settings. Everything that reaches the economy, other players or
 * the shared world is walled — and so is the developer tooling.
 */
export const GUEST_PANEL_ACCESS: Readonly<Record<string, GuestPanelAccess>> = {
  character: 'allowed',
  inventory: 'allowed',
  equipment: 'allowed',
  progression: 'allowed',
  quests: 'allowed',
  crafting: 'allowed',
  settings: 'allowed',
  accessibility: 'allowed',
  trade: 'walled',
  marketplace: 'walled',
  store: 'walled',
  social: 'walled',
  guild: 'walled',
  mail: 'walled',
  housing: 'walled',
  endgame: 'walled',
  debug: 'walled',
  playtest: 'walled',
};

/** Whether a guest may open this panel. Unknown ids are walled (fail closed). */
export function guestPanelAllowed(id: string): boolean {
  return GUEST_PANEL_ACCESS[id] === 'allowed';
}

/** The one line a guest sees when something reaches the wall. */
export const GUEST_WALL_LINE = 'That is not part of the demo — it opens with your real adventure.';

/** A repeat of the same refused type is silent for this long. */
export const WALL_REPEAT_MS = 60_000;
/** At most one wall line in this window, whatever the type. */
export const WALL_QUIET_MS = 8_000;

/**
 * De-duplicates `guest_wall` refusals into at most one calm line: never twice for the same type
 * within `WALL_REPEAT_MS`, never more than one in any `WALL_QUIET_MS`. `note` returns the line to
 * show, or null to stay quiet.
 */
export class WallNotifier {
  private readonly lastByType = new Map<string, number>();
  private lastShownMs = Number.NEGATIVE_INFINITY;

  note(type: string, nowMs: number): string | null {
    const last = this.lastByType.get(type);
    this.lastByType.set(type, nowMs);
    if (last !== undefined && nowMs - last < WALL_REPEAT_MS) return null;
    if (nowMs - this.lastShownMs < WALL_QUIET_MS) return null;
    this.lastShownMs = nowMs;
    return GUEST_WALL_LINE;
  }
}

/**
 * Whether the guest's kit can open a node of `resource` — the server's rank gate
 * (`MasteryService.gatherTierLockedFor`, AD-418) read off the wire, so the guest's "[E] Gather"
 * prompt never offers a node it would only be refused (`tierLocked`). The echo zone tables
 * mining and forestry rows the kit has no rank for; the demo's goal is the fenroot stand, and a
 * locked ore vein must not look like one. Display only — the server still judges every gather.
 *
 * Same fail-open shape as the server: an unknown resource or a tier ≤ 1 is gatherable; a skill
 * with no track yet is rank 1, whose band admits tier 1.
 */
export function guestCanGather(
  resource: string,
  tiers: Readonly<Record<string, number>>,
  skills: Readonly<Record<string, string>>,
  masteries: readonly { kind: string; trackId: string; tierAccess: number }[],
): boolean {
  const tier = tiers[resource];
  if (tier === undefined || !Number.isFinite(tier) || tier <= 1) return true;
  const skill = skills[resource];
  if (!skill) return true;
  const track = masteries.find((t) => t.kind === 'gathering' && t.trackId === skill);
  return tier <= (track?.tierAccess ?? 1);
}
